import { lessAgressiveColorMapper } from 'components/PostTrade/ProductModification/components/ValidationErrors.utils';
import { LastUpdate } from 'components/secondaryMarket/live/sidePanel/LastUpdate';
import { ConnectedBidAsk } from 'components/secondaryMarket/shared/SidePanel/BidAsk';
import { FlexColumnPanel } from 'components/secondaryMarket/shared/SidePanel/FlexColumnPanel';
import { Messages } from 'components/secondaryMarket/shared/SidePanel/Messages';
import { ActionButton, ButtonPanel, MessagesPanel } from 'components/secondaryMarket/shared/status';
import { KycScore } from 'components/secondaryMarket/shared/status/KycScore';
import { PanelFooter, PanelHeader } from 'components/secondaryMarket/shared/status/Panel';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { KycDetail, KycScoreStatus } from 'state/secondaryMarket/shared/kyc/kyc.model';
import type { AppState } from 'state/state.model';

interface BeforeQuotedSidePanelProps<F = Form> {
  tabId: string;
  errors: readonly SecondaryMarketFormError<F>[]; //
  errorMessage?: string; //
  kycScore: KycScoreStatus | undefined; //
  kycDetails: readonly KycDetail[] | undefined; //
  timeout?: number;
  canQuote?: boolean;
  backendReports: readonly SecondaryMarketFormError<F>[];
  status: 'READY' | 'NOT_READY';
  salesMessage?: string;
  traderMessage?: string;

  isLimitOrder?: boolean;
  onQuote(): void;
}
export function StreamingBeforeQuotedSidePanel<F = Form>({
  tabId,
  status,
  onQuote,
  kycDetails,
  kycScore,
  canQuote,
  backendReports,
  errors,
  salesMessage,
  traderMessage,
  isLimitOrder = false,
}: BeforeQuotedSidePanelProps<F>) {
  const [isTraderMessageHidden, setHideTraderMessage] = useState(false);
  const [isSalesMessageHidden, setHideSalesMessage] = useState(false);

  const isKycHidden = useToggleFeature('HideKYCDisplayParis');
  const textColor = status === 'READY' ? undefined : 'secondary';
  const isLive = useSelector((state: AppState) => liveSelectors.getStreamingStatus(state, tabId!));
  return (
    <FlexColumnPanel>
      <PanelHeader textColor={textColor} color="lvl1" discreet className="border border-bottom-0 p-0">
        <ConnectedBidAsk tabId={tabId} />
      </PanelHeader>
      <PanelFooter
        textColor={textColor}
        color="lvl1"
        statusKey="secondaryMarket.sidePanel.label.notReady"
        discreet
        icon={status === 'READY' ? 'offline_bolt' : 'not_ready'}
        className="border"
        live={isLive}
      >
        <LastUpdate tabId={tabId} />
      </PanelFooter>
      {!isKycHidden && kycScore !== undefined && <KycScore className="m-2" status={kycScore} details={kycDetails} />}
      <MessagesPanel
        errors={[...errors, ...backendReports]}
        className="flex-grow-1 pt-2"
        e2e="side-panel-header"
        colorToSeverity={lessAgressiveColorMapper}
      >
        <Messages
          salesMessage={salesMessage}
          isSalesMessageHidden={isSalesMessageHidden}
          hideSalesMessage={() => setHideSalesMessage(true)}
          traderMessage={traderMessage}
          isTraderMessageHidden={isTraderMessageHidden}
          hideTraderMessage={() => setHideTraderMessage(true)}
        />
      </MessagesPanel>
      <ButtonPanel>
        <ActionButton
          color="primary"
          label={
            isLimitOrder ? 'secondaryMarket.sidePanel.label.placeLimitOrder' : 'secondaryMarket.sidePanel.label.quote'
          }
          disabled={status === 'NOT_READY' || !canQuote}
          onClick={onQuote}
        />
      </ButtonPanel>
    </FlexColumnPanel>
  );
}
